import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import urlpath from '../../urlpath.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const sessionId = localStorage.getItem('hr_session_id');
      const userData = localStorage.getItem('hr_user');
      
      if (sessionId && userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Verify session is still valid
        verifySession(sessionId);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('AuthContext: Error checking auth status:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const verifySession = async (sessionId) => {
    try {
      const response = await axios.get(urlpath + '/api/verify-session', {
        headers: {
          'X-Session-ID': sessionId
        }
      });

      if (response.data.success) {
        // Session verified successfully
      } else {
        logout();
      }
    } catch (error) {
      console.error('AuthContext: Session verification failed with error:', error);
      console.error('AuthContext: Error response:', error.response?.data);
      logout();
    }
  };

  const login = async (user_id, password) => {
    try {     
      
      const response = await axios.post(urlpath + '/api/login', {
        user_id: user_id.trim(),
        password: password
      });

      if (response.data.success) {
        const { sessionId, user: userData } = response.data;
        
        // Store session ID and user data
        localStorage.setItem('hr_session_id', sessionId);
        localStorage.setItem('hr_user', JSON.stringify({
          id: userData.user_id,
          user_id: userData.user_id,
          role: userData.role,
          status: userData.status,
          loginTime: new Date().toISOString()
        }));

        // Set user state
        const userState = {
          id: userData.user_id,
          user_id: userData.user_id,
          role: userData.role,
          status: userData.status,
          loginTime: new Date().toISOString()
        };
        
        setUser(userState);
        
        return { success: true, user: userState };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please try again.' 
      };
    }
  };

  const logout = async () => {
    try {
      const sessionId = localStorage.getItem('hr_session_id');
      if (sessionId) {
        // Notify server about logout
        await axios.post(urlpath + '/api/logout', {}, {
          headers: {
            'X-Session-ID': sessionId
          }
        });
      }
      
      // Clear localStorage
      localStorage.removeItem('hr_session_id');
      localStorage.removeItem('hr_user');
      
      // Clear state
      setUser(null);
      
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
      // Clear local data even if server request fails
      localStorage.removeItem('hr_session_id');
      localStorage.removeItem('hr_user');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
